import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';

/**
 * BottomSheet — composant générique avec 3 variants.
 *
 *   variant="fixed"      → fond blanc fixe ancré en bas, pas de swipe
 *   variant="swipeable"  → 4 états (hidden → list → summary → expanded) pilotés par prop `state`
 *   variant="modal"      → overlay sombre + dismiss via bouton ou Esc
 *
 * Respecte prefers-reduced-motion : transitions désactivées dans ce cas.
 */

const STATES = {
  hidden:   { translate: '100%', height: 0 },
  list:     { translate: '0%',   height: '44vh' },
  summary:  { translate: '0%',   height: '240px' },
  expanded: { translate: '0%',   height: '72vh' },
};

export default function BottomSheet({
  variant = 'fixed',
  state = 'list',
  onClose,
  onStateChange,
  children,
  ariaLabel,
}) {
  const sheetRef = useRef(null);
  const startYRef = useRef(null);

  useEffect(() => {
    if (variant !== 'modal') return undefined;
    function onKey(e) {
      if (e.key === 'Escape' && onClose) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [variant, onClose]);

  function onTouchStart(e) {
    if (variant !== 'swipeable') return;
    startYRef.current = e.touches[0].clientY;
  }
  function onTouchEnd(e) {
    if (variant !== 'swipeable' || startYRef.current == null) return;
    const endY = e.changedTouches[0].clientY;
    const delta = endY - startYRef.current;
    startYRef.current = null;

    if (Math.abs(delta) < 40 || !onStateChange) return;

    if (delta < 0) {
      if (state === 'list') onStateChange('expanded');
      else if (state === 'summary') onStateChange('expanded');
    } else {
      if (state === 'expanded') onStateChange('summary');
      else if (state === 'summary' || state === 'list') onStateChange('hidden');
    }
  }

  if (variant === 'modal') {
    return (
      <ModalOverlay onClick={onClose} role="dialog" aria-modal="true" aria-label={ariaLabel}>
        <ModalSheet onClick={e => e.stopPropagation()}>
          <Handle />
          {children}
        </ModalSheet>
      </ModalOverlay>
    );
  }

  if (variant === 'swipeable') {
    const s = STATES[state] || STATES.list;
    return (
      <SwipeableSheet
        ref={sheetRef}
        style={{ transform: `translateY(${s.translate})`, height: s.height }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        role="region"
        aria-label={ariaLabel}
      >
        <Handle />
        {children}
      </SwipeableSheet>
    );
  }

  return (
    <FixedSheet role="region" aria-label={ariaLabel}>
      {children}
    </FixedSheet>
  );
}

const baseSheet = `
  background: #FFFFFF;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 32px rgba(0,0,0,0.12);
  padding: 16px 20px 28px;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const FixedSheet = styled.div`
  ${baseSheet}
  position: relative;
  z-index: 2;
`;

const SwipeableSheet = styled.div`
  ${baseSheet}
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  overflow: hidden;
  transition: transform 0.3s ease, height 0.3s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(27,46,40,0.55);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const ModalSheet = styled.div`
  ${baseSheet}
  padding: 20px 20px 40px;
`;

const Handle = styled.div`
  width: 32px;
  height: 4px;
  border-radius: 2px;
  background: ${p => p.theme.palette.border.main};
  margin: 0 auto 12px;
`;
